import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { leaveAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../components/EmployeeManagement.css';

const LeaveManagement = () => {
  const { currentUser, isPrivileged } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState({ casual: {}, sick: {}, annual: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ employee: '', leaveType: 'casual', startDate: '', endDate: '', reason: '' });
  const [activeTab, setActiveTab] = useState('requests');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const res = await leaveAPI.getAll();
      setLeaves(res?.data || res || []);
      if (currentUser?._id) {
        const bal = await leaveAPI.getBalance(currentUser._id).catch(() => null);
        if (bal?.data) setBalance(bal.data);
      }
    } catch (e) { setError('Failed to load leaves'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLeaves(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.employee) {
        payload.employee = currentUser?._id || currentUser?.id;
      }
      await leaveAPI.apply(payload);
      setShowModal(false);
      setForm({ employee: '', leaveType: 'casual', startDate: '', endDate: '', reason: '' });
      setError('');
      fetchLeaves();
    } catch (err) {
      setError(err?.message || err?.error || (typeof err === 'string' ? err : 'Failed to apply leave'));
    }
  };

  const handleStatus = async (id, status) => {
    try { await leaveAPI.updateStatus(id, { status }); fetchLeaves(); }
    catch (e) { setError('Failed to update status'); }
  };

  const totalRemaining = Object.values(balance).reduce((s, v) => s + (v.remaining || 0), 0);
  const totalUsed = Object.values(balance).reduce((s, v) => s + (v.used || 0), 0);
  const totalLeave = Object.values(balance).reduce((s, v) => s + (v.total || 0), 0);
  const pendingCount = leaves.filter(l => l.status === 'pending').length;

  const getEmployeeSummaries = () => {
    const summaryMap = {};
    leaves.forEach(leave => {
      if (!leave.employee) return;
      const empId = leave.employee._id || leave.employee.id || String(leave.employee);
      const name = leave.employee.name || 'Unknown';
      const empIdStr = leave.employee.employeeId || '—';
      const days = Number(leave.days) || 0;
      
      if (!summaryMap[empId]) {
        summaryMap[empId] = {
          name,
          employeeId: empIdStr,
          totalApprovedLeaves: 0,
          totalApprovedDays: 0,
          pendingLeaves: 0,
          rejectedLeaves: 0
        };
      }
      
      if (leave.status === 'approved') {
        summaryMap[empId].totalApprovedLeaves += 1;
        summaryMap[empId].totalApprovedDays += days;
      } else if (leave.status === 'pending') {
        summaryMap[empId].pendingLeaves += 1;
      } else if (leave.status === 'rejected') {
        summaryMap[empId].rejectedLeaves += 1;
      }
    });

    const list = Object.values(summaryMap);
    if (!searchQuery) return list;
    return list.filter(item => 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      item.employeeId.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const employeeSummaries = getEmployeeSummaries();
  const leaveData = leaves;

  return (
    <div className="leave-management">
      <div className="module-header">
        <h2>Leave Management</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <i className="fas fa-plus"></i> Apply Leave
        </button>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      <div className="leave-stats">
        <div className="leave-stat"><h4>Total Leave</h4><p>{totalLeave} days</p></div>
        <div className="leave-stat"><h4>Used</h4><p>{totalUsed} days</p></div>
        <div className="leave-stat"><h4>Remaining</h4><p>{totalRemaining} days</p></div>
        <div className="leave-stat"><h4>Pending Approval</h4><p>{pendingCount} requests</p></div>
      </div>

      {isPrivileged && (
        <div className="tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              className={`btn ${activeTab === 'requests' ? 'btn-primary' : 'btn-secondary'}`} 
              onClick={() => setActiveTab('requests')}
            >
              <i className="fas fa-file-alt"></i> Leave Applications
            </button>
            <button 
              className={`btn ${activeTab === 'summary' ? 'btn-primary' : 'btn-secondary'}`} 
              onClick={() => setActiveTab('summary')}
            >
              <i className="fas fa-users"></i> Employee Leaves Summary
            </button>
          </div>
          {activeTab === 'summary' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-search" style={{ color: '#9ca3af' }}></i>
              <input
                type="text"
                placeholder="Search employee..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  outline: 'none',
                  width: '200px'
                }}
              />
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }}></i><p>Loading...</p></div>
      ) : activeTab === 'requests' ? (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {isPrivileged && <th>Employee</th>}
                <th>Leave Type</th>
                <th>From Date</th>
                <th>To Date</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leaveData.length === 0
                ? <tr><td colSpan="8" style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>No leave records found</td></tr>
                : leaveData.map(leave => (
                  <tr key={leave._id}>
                    {isPrivileged && <td>{leave.employee?.name || '—'}</td>}
                    <td style={{ textTransform: 'capitalize' }}>{leave.leaveType}</td>
                    <td>{leave.startDate ? new Date(leave.startDate).toLocaleDateString() : '—'}</td>
                    <td>{leave.endDate ? new Date(leave.endDate).toLocaleDateString() : '—'}</td>
                    <td>{leave.days || '—'}</td>
                    <td>{leave.reason}</td>
                    <td>
                      <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
                        background: leave.status === 'approved' ? '#d1fae5' : leave.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                        color: leave.status === 'approved' ? '#065f46' : leave.status === 'rejected' ? '#991b1b' : '#92400e'
                      }}>{leave.status}</span>
                    </td>
                    <td>
                      {isPrivileged && leave.status === 'pending' ? (
                        <>
                          <button className="btn-small btn-success" onClick={() => handleStatus(leave._id, 'approved')}>Approve</button>
                          <button className="btn-small btn-danger" onClick={() => handleStatus(leave._id, 'rejected')}>Reject</button>
                        </>
                      ) : (
                        <button className="btn-icon view"><i className="fas fa-eye"></i></button>
                      )}
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee Name</th>
                <th>Employee ID</th>
                <th>Approved Leaves Count</th>
                <th>Total Approved Leave Days</th>
                <th>Pending Requests</th>
                <th>Rejected Requests</th>
              </tr>
            </thead>
            <tbody>
              {employeeSummaries.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>No employee summaries found</td></tr>
              ) : (
                employeeSummaries.map(emp => (
                  <tr key={emp.employeeId}>
                    <td><strong>{emp.name}</strong></td>
                    <td>{emp.employeeId}</td>
                    <td>
                      <span className="badge badge-success" style={{ background: '#d1fae5', color: '#065f46', padding: '4px 10px', borderRadius: 12, fontWeight: 600 }}>
                        {emp.totalApprovedLeaves} Requests
                      </span>
                    </td>
                    <td>
                      <strong>{emp.totalApprovedDays} Days</strong>
                    </td>
                    <td>
                      <span className="badge" style={{ background: '#fef3c7', color: '#92400e', padding: '4px 10px', borderRadius: 12, fontWeight: 600 }}>
                        {emp.pendingLeaves} Pending
                      </span>
                    </td>
                    <td>
                      <span className="badge" style={{ background: '#fee2e2', color: '#991b1b', padding: '4px 10px', borderRadius: 12, fontWeight: 600 }}>
                        {emp.rejectedLeaves} Rejected
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showModal} onClose={() => setShowModal(false)} title="Apply for Leave">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Leave Type</label>
            <select className="form-input" value={form.leaveType} onChange={e => setForm({ ...form, leaveType: e.target.value })} required>
              <option value="casual">Casual Leave</option>
              <option value="sick">Sick Leave</option>
              <option value="annual">Annual Leave</option>
              <option value="maternity">Maternity Leave</option>
              <option value="paternity">Paternity Leave</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>From Date</label>
              <input type="date" className="form-input" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>To Date</label>
              <input type="date" className="form-input" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} required />
            </div>
          </div>
          <div className="form-group">
            <label>Reason</label>
            <textarea className="form-input" rows="3" placeholder="Enter reason..." value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} required></textarea>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Apply Leave</button>
          </div>
        </form>
      </Modal>

      <style jsx>{`
        .leave-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }

        .leave-stat {
          background: white;
          padding: 20px;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .leave-stat h4 {
          color: var(--text-secondary);
          font-size: 14px;
          margin-bottom: 10px;
        }

        .leave-stat p {
          font-size: 28px;
          font-weight: 700;
          color: var(--primary-color);
        }

        .btn-small {
          padding: 6px 12px;
          font-size: 12px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          margin-right: 5px;
        }

        .btn-success {
          background: var(--success-color);
          color: white;
        }

        .btn-danger {
          background: var(--danger-color);
          color: white;
        }
      `}</style>
    </div>
  );
};

export default LeaveManagement;
